import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { resetALL } from "../../../../../../../.globals/redux/actions";
import { endpoints } from "../../../../../../../core/defaultValues";
import {
    clientQuery,
} from "../../../../../../../core/data/GraphQLApi";
import { ExceptionManager } from "../../../../../../../core/logManager";

const INIT_STATE = {
    open: false,
    loading: false,
    location: null,
    contact: null,
    customers: [],
    loadingCustomers: false,
    contacts: [],
    loadingContacts: false,
    locations: [],
    loadingLocations: false,
    selectedCustomer: null,
    locationId: null,
    contactId: null,
    customerId: null
};

const route = "modules/workOrders/modals/customerSelector/redux/slice";

export const workOrdersSelectorCustomerSearchCustomers = createAsyncThunk(
    "workOrders/customerSelector/searchCustomers",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query ($conditions: [CustomerSearchInput], $sort: [CustomerSortInput], $limit: Int, $offset: Int) {
                    data: searchCustomers(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        offset: $offset
                    ) {
                        total
                        items {
                            id
                            name
                            customerContact {
                                id
                                name
                                phone
                                email
                            }
                            customerLocation {
                                id
                                name
                                address
                            }
                            tagIds
                            tags {
                                id
                            }
                        }
                    }
                }
                `,
                {
                    conditions: payload?.conditions,
                    sort: { field: "name", order: "ASC" }
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data?.items || []
        } catch (exc) {
            ExceptionManager(exc, route, "searchCustomers");
            return rejectWithValue(exc);
        }
    }
);

export const workOrdersSelectorCustomerSearchContacts = createAsyncThunk(
    "workOrders/customerSelector/searchEntityContacts",
    async ({ customerId }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query ($conditions: [EntityContactSearchInput], $sort: [EntityContactSortInput], $limit: Int, $offset: Int) {
                    data: searchEntityContacts(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        offset: $offset
                    ) {
                        total
                        items {
                            id
                            name
                            phone
                            email
                        }
                    }
                }
                `,
                {
                    conditions: [{ fields: ["entityId"], terms: [customerId], exact: true }],
                    sort: { field: "name", order: "ASC" }
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data?.items || []
        } catch (exc) {
            ExceptionManager(exc, route, "searchEntityContacts");
            return rejectWithValue(exc);
        }
    }
);

export const workOrdersSelectorCustomerSearchLocations = createAsyncThunk(
    "workOrders/customerSelector/searchAvailableDrivers",
    async ({ customerId }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query ($conditions: [CustomerLocationSearchInput], $sort: [CustomerLocationSortInput], $limit: Int, $offset: Int) {
                    data: searchCustomerLocations(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        offset: $offset
                    ) {
                        total
                        items {
                            id
                            name
                            address
                        }
                    }
                }
                `,
                {
                    conditions: [{ fields: ["customerId"], terms: [customerId], exact: true }],
                    sort: { field: "name", order: "ASC" }
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data?.items || []
        } catch (exc) {
            ExceptionManager(exc, route, "workOrdersSelectorCustomerSearchCustomers");
            return rejectWithValue(exc);
        }
    }
);

const workOrdersCustomerSelectorRedux = createSlice({
    name: "workOrders/customerSelector",
    initialState: INIT_STATE,
    reducers: {
        resetWorkOrderCustomerSelector(state, action) {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        },
        openModalWorkOrderCustomerSelector(state, action) {
            state.open = true;
            state.selectedCustomer = action.payload.selectedCustomer
        },
        setFieldIdCustomerSelector(state, action) {
            state[action?.payload?.field] = action?.payload?.value
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //workOrdersSelectorCustomerSearchCustomers
        builder.addCase(workOrdersSelectorCustomerSearchCustomers.pending, (state) => {
            state.loadingCustomers = true;
        });
        builder.addCase(workOrdersSelectorCustomerSearchCustomers.fulfilled, (state, action) => {
            state.loadingCustomers = false;
            state.customers = action.payload;
        });
        builder.addCase(workOrdersSelectorCustomerSearchCustomers.rejected, (state, action) => {
            state.loadingCustomers = false;
            state.error = action.payload;
        });

        //workOrdersSelectorCustomerSearchContacts
        builder.addCase(workOrdersSelectorCustomerSearchContacts.pending, (state) => {
            state.loadingContacts = true;
        });
        builder.addCase(workOrdersSelectorCustomerSearchContacts.fulfilled, (state, action) => {
            state.loadingContacts = false;
            state.contacts = action.payload;
        });
        builder.addCase(workOrdersSelectorCustomerSearchContacts.rejected, (state, action) => {
            state.loadingContacts = false;
            state.error = action.payload;
        });

        //workOrdersSelectorCustomerSearchLocations
        builder.addCase(workOrdersSelectorCustomerSearchLocations.pending, (state) => {
            state.loadingLocations = true;
        });
        builder.addCase(workOrdersSelectorCustomerSearchLocations.fulfilled, (state, action) => {
            state.loadingLocations = false;
            state.locations = action.payload;
        });
        builder.addCase(workOrdersSelectorCustomerSearchLocations.rejected, (state, action) => {
            state.loadingLocations = false;
            state.error = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = workOrdersCustomerSelectorRedux;

// Extract and export each action creator by name
export const {
    resetWorkOrderCustomerSelector,
    openModalWorkOrderCustomerSelector,
    setFieldIdCustomerSelector,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
